/**
 * Séquence de difficulté pour le parcours : F I D F I D E (puis on recommence).
 * Utilisée pour "Un exercice au hasard" (parcours et optionnellement Horizons).
 * Plafond par étage : seules les difficultés autorisées sont proposées (pas d'élargissement hors plafond).
 */

import { getNodeDef } from '../data/parcoursTree'

const PARCOURS_DIFFICULTY_SEQUENCE = [
  'facile',
  'intermédiaire',
  'difficile',
  'facile',
  'intermédiaire',
  'difficile',
  'expert'
]

/** Difficultés autorisées en priorité selon l’étage du nœud (1–4). */
export const STAGE_ALLOWED_DIFFICULTIES = {
  1: ['facile'],
  2: ['facile', 'intermédiaire'],
  3: ['facile', 'intermédiaire', 'difficile'],
  4: ['facile', 'intermédiaire', 'difficile', 'expert']
}

export const PARCOURS_DIFFICULTY_SEQUENCE_INDEX_KEY = 'parcours_difficulty_sequence_index'

/**
 * Difficultés autorisées (plafond) pour un nœud parcours.
 * Nœud inconnu → étage 4 (tout ouvert).
 * @param {string} nodeId
 * @returns {string[]}
 */
export function getAllowedDifficultiesForNode(nodeId) {
  const stage = getNodeDef(nodeId)?.stage
  const allowed = STAGE_ALLOWED_DIFFICULTIES[stage]
  return allowed ? [...allowed] : [...STAGE_ALLOWED_DIFFICULTIES[4]]
}

/**
 * Si preferred est dans allowed, la renvoie ; sinon la prochaine difficulté
 * autorisée en suivant l’ordre circulaire de PARCOURS_DIFFICULTY_SEQUENCE.
 * @param {string} preferred
 * @param {string[]} allowed
 * @returns {string|null}
 */
export function clampDifficultyToAllowed(preferred, allowed) {
  if (!Array.isArray(allowed) || allowed.length === 0) return preferred || null
  const allowedSet = new Set(allowed)
  if (preferred && allowedSet.has(preferred)) return preferred

  const len = PARCOURS_DIFFICULTY_SEQUENCE.length
  let start = preferred ? PARCOURS_DIFFICULTY_SEQUENCE.indexOf(preferred) : 0
  if (start < 0) start = 0

  for (let i = 0; i < len; i++) {
    const d = PARCOURS_DIFFICULTY_SEQUENCE[(start + i) % len]
    if (allowedSet.has(d)) return d
  }
  return allowed[0]
}

/**
 * Retourne la difficulté préférée pour le prochain tirage et avance l'index (modulo 7).
 * À appeler côté UI avant getExercisesForNode / getRandomPublicExercise.
 * @returns {string} 'facile' | 'intermédiaire' | 'difficile' | 'expert'
 */
export function getNextPreferredDifficulty() {
  if (typeof sessionStorage === 'undefined') {
    return PARCOURS_DIFFICULTY_SEQUENCE[0]
  }
  const key = PARCOURS_DIFFICULTY_SEQUENCE_INDEX_KEY
  let index = 0
  try {
    const stored = sessionStorage.getItem(key)
    if (stored != null) index = Math.max(0, Math.min(6, parseInt(stored, 10)))
    if (Number.isNaN(index)) index = 0
  } catch {
    index = 0
  }
  const difficulty = PARCOURS_DIFFICULTY_SEQUENCE[index]
  const nextIndex = (index + 1) % 7
  try {
    sessionStorage.setItem(key, String(nextIndex))
  } catch {
    // ignore
  }
  return difficulty
}

/**
 * Ordre des difficultés à essayer quand le pool n'a pas d'exercice à la difficulté préférée :
 * préférée d'abord, puis le reste de la séquence (ordre F I D F I D E).
 * Si allowedDifficulties est fourni, restreint au plafond (dédupliqué), sans fallback hors plafond.
 * @param {string} preferredDifficulty
 * @param {string[]} [allowedDifficulties]
 * @returns {string[]}
 */
export function getDifficultyTryOrder(preferredDifficulty, allowedDifficulties) {
  const hasAllowed = Array.isArray(allowedDifficulties) && allowedDifficulties.length > 0

  if (hasAllowed) {
    const allowedSet = new Set(allowedDifficulties)
    const clamped = clampDifficultyToAllowed(preferredDifficulty, allowedDifficulties)
    const len = PARCOURS_DIFFICULTY_SEQUENCE.length
    let start = clamped ? PARCOURS_DIFFICULTY_SEQUENCE.indexOf(clamped) : 0
    if (start < 0) start = 0

    const order = []
    const seen = new Set()
    for (let i = 0; i < len; i++) {
      const d = PARCOURS_DIFFICULTY_SEQUENCE[(start + i) % len]
      if (allowedSet.has(d) && !seen.has(d)) {
        seen.add(d)
        order.push(d)
      }
    }
    return order
  }

  if (!preferredDifficulty) return [...PARCOURS_DIFFICULTY_SEQUENCE]
  const rest = PARCOURS_DIFFICULTY_SEQUENCE.filter(d => d !== preferredDifficulty)
  return [preferredDifficulty, ...rest]
}

export { PARCOURS_DIFFICULTY_SEQUENCE }
